import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Copy, Download, KeyRound } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

function BackupCodes({ codes }) {
  const copy = () => {
    Promise.resolve(navigator.clipboard?.writeText(codes.join("\n")))
      .then(() => toast.success("Backup codes copied."))
      .catch(() => toast.error("Couldn't copy — save them manually."));
  };
  const download = () => {
    const blob = new Blob([`Startup Otter — two-factor backup codes\n\n${codes.join("\n")}\n\nEach code works once. Keep them private.\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "startup-otter-backup-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-secondary/50 p-3 font-mono text-sm" data-testid="mfa-backup-codes">
        {codes.map((c) => <span key={c} className="tracking-wider text-foreground">{c}</span>)}
      </div>
      <div className="mt-3 flex gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={copy} data-testid="mfa-copy-codes"><Copy className="mr-1.5 h-3.5 w-3.5" />Copy</Button>
        <Button type="button" variant="secondary" size="sm" onClick={download} data-testid="mfa-download-codes"><Download className="mr-1.5 h-3.5 w-3.5" />Download</Button>
      </div>
    </div>
  );
}

export function MfaSettings() {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [dialog, setDialog] = useState(null);       // "enable" | "regenerate" | "disable"
  const [step, setStep] = useState("password");     // enable flow: password -> scan -> codes
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState(null);         // {secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/auth/mfa/status"); setStatus(data); } catch { /* no-op */ }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setStep("password"); setPassword(""); setSetup(null); setCode(""); setCodes(null); setUseBackup(false); setBusy(false); };
  const closeDialog = (open) => { if (!open) { setDialog(null); reset(); } };
  const open = (which) => { reset(); setDialog(which); };
  const fail = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

  const startSetup = async (e) => {
    e?.preventDefault?.(); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup/start", { password }); setSetup(data); setStep("scan"); }
    catch (e2) { fail(e2, "Couldn't start setup."); } finally { setBusy(false); }
  };
  const verifySetup = async (e) => {
    e?.preventDefault?.(); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup/verify", { code }); setCodes(data.backup_codes); setStep("codes"); }
    catch (e2) { fail(e2, "That code is incorrect."); } finally { setBusy(false); }
  };
  const finishEnable = async () => { setDialog(null); reset(); await load(); await refreshUser?.(); toast.success("Two-factor authentication enabled."); };

  const regenerate = async (e) => {
    e?.preventDefault?.(); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/backup/regenerate", { password, code, is_backup: useBackup }); setCodes(data.backup_codes); }
    catch (e2) { fail(e2, "Couldn't regenerate codes."); } finally { setBusy(false); }
  };
  const finishRegen = async () => { setDialog(null); reset(); await load(); toast.success("New backup codes generated."); };

  const disableMfa = async (e) => {
    e?.preventDefault?.(); setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password, code, is_backup: useBackup });
      setDialog(null); reset(); await load(); await refreshUser?.();
      toast.success("Two-factor authentication disabled.");
    } catch (e2) { fail(e2, "Couldn't disable two-factor authentication."); } finally { setBusy(false); }
  };

  const manageFields = (
    <div className="mt-4 space-y-3">
      <div className="space-y-1.5"><Label>Password</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mfa-manage-password" />
      </div>
      <div className="space-y-1.5"><Label>{useBackup ? "Backup code" : "Authenticator code"}</Label>
        <Input inputMode={useBackup ? "text" : "numeric"} value={code}
          onChange={(e) => setCode(useBackup ? e.target.value : e.target.value.replace(/\D/g, ""))}
          placeholder={useBackup ? "xxxx-xxxx-xxxx-xxxx-xxxx" : "123456"} data-testid="mfa-manage-code" />
      </div>
      <button type="button" className="text-xs text-primary hover:underline" onClick={() => { setUseBackup((v) => !v); setCode(""); }} data-testid="mfa-manage-backup-toggle">
        {useBackup ? "Use an authenticator code instead" : "Use a backup code instead"}
      </button>
    </div>
  );

  const enabled = status?.mfa_enabled;

  return (
    <Card className="p-6" data-testid="mfa-settings-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
            <ShieldCheck className="h-5 w-5 text-primary" /> Two-factor authentication
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Add a second step at sign-in using an authenticator app (Google Authenticator, Authy, 1Password, Microsoft Authenticator).</p>
        </div>
        {status && (enabled
          ? <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="mfa-status-badge">Enabled</Badge>
          : <Badge variant="secondary" data-testid="mfa-status-badge">Not enabled</Badge>)}
      </div>

      {status?.mfa_required && !enabled && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="mfa-required-note">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Your role requires two-factor authentication for admin access. Enable it to keep using admin tools.</span>
        </div>
      )}

      {enabled ? (
        <div className="mt-5 space-y-4">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div><span className="text-muted-foreground">Method</span><div className="font-[560] text-foreground">Authenticator app (TOTP)</div></div>
            <div><span className="text-muted-foreground">Enabled</span><div className="font-[560] text-foreground">{status.enabled_at ? new Date(status.enabled_at).toLocaleDateString() : "—"}</div></div>
            <div><span className="text-muted-foreground">Backup codes left</span><div className="font-[560] text-foreground" data-testid="mfa-backup-remaining">{status.backup_codes_remaining}</div></div>
          </div>
          {status.backup_codes_remaining <= 2 && (
            <p className="text-xs text-amber-600">You're running low on backup codes — regenerate to get a fresh set.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => open("regenerate")} data-testid="mfa-regenerate-button"><KeyRound className="mr-1.5 h-3.5 w-3.5" />Regenerate backup codes</Button>
            <Button type="button" variant="outline" size="sm" className="border-destructive/40 text-destructive hover:bg-destructive/5" onClick={() => open("disable")} data-testid="mfa-disable-button">Disable 2FA</Button>
          </div>
        </div>
      ) : (
        <div className="mt-5">
          <Button type="button" onClick={() => open("enable")} data-testid="mfa-enable-button"><ShieldCheck className="mr-1.5 h-4 w-4" />Enable two-factor authentication</Button>
        </div>
      )}

      {/* Enable flow */}
      <Dialog open={dialog === "enable"} onOpenChange={closeDialog}>
        <DialogContent data-testid="mfa-enable-dialog">
          {step === "password" && (
            <form onSubmit={startSetup}>
              <DialogHeader>
                <DialogTitle>Confirm your password</DialogTitle>
                <DialogDescription>For your security, confirm your password to begin setting up two-factor authentication.</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-1.5"><Label>Password</Label>
                <Input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mfa-password-input" />
              </div>
              <DialogFooter className="mt-5"><Button type="submit" disabled={busy || !password} data-testid="mfa-setup-start-submit">{busy ? "Please wait…" : "Continue"}</Button></DialogFooter>
            </form>
          )}
          {step === "scan" && setup && (
            <form onSubmit={verifySetup}>
              <DialogHeader>
                <DialogTitle>Scan the QR code</DialogTitle>
                <DialogDescription>Scan this with your authenticator app, then enter the 6-digit code it shows.</DialogDescription>
              </DialogHeader>
              <div className="mt-4 flex flex-col items-center gap-3">
                <div className="rounded-lg bg-white p-3" data-testid="mfa-qr"><QRCodeSVG value={setup.otpauth_uri} size={168} /></div>
                <div className="w-full text-center">
                  <p className="text-xs text-muted-foreground">Can't scan? Enter this key manually:</p>
                  <code className="mt-1 inline-block break-all rounded bg-secondary px-2 py-1 font-mono text-xs" data-testid="mfa-secret-key">{setup.secret}</code>
                </div>
                <div className="w-full space-y-1.5"><Label>6-digit code</Label>
                  <Input inputMode="numeric" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" data-testid="mfa-verify-code-input" />
                </div>
              </div>
              <DialogFooter className="mt-5"><Button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-setup-verify-submit">{busy ? "Verifying…" : "Verify & enable"}</Button></DialogFooter>
            </form>
          )}
          {step === "codes" && codes && (
            <div>
              <DialogHeader>
                <DialogTitle>Save your backup codes</DialogTitle>
                <DialogDescription>Store these somewhere safe. Each code works once and this is the only time they'll be shown — use them to sign in if you lose your authenticator device.</DialogDescription>
              </DialogHeader>
              <div className="mt-4"><BackupCodes codes={codes} /></div>
              <DialogFooter className="mt-5"><Button type="button" onClick={finishEnable} data-testid="mfa-codes-done">I've saved my codes</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Regenerate flow */}
      <Dialog open={dialog === "regenerate"} onOpenChange={closeDialog}>
        <DialogContent data-testid="mfa-regenerate-dialog">
          {!codes ? (
            <form onSubmit={regenerate}>
              <DialogHeader>
                <DialogTitle>Regenerate backup codes</DialogTitle>
                <DialogDescription>This invalidates your existing backup codes. Confirm your password and a current code to continue.</DialogDescription>
              </DialogHeader>
              {manageFields}
              <DialogFooter className="mt-5"><Button type="submit" disabled={busy || !password || code.length < 6} data-testid="mfa-regenerate-submit">{busy ? "Please wait…" : "Regenerate"}</Button></DialogFooter>
            </form>
          ) : (
            <div>
              <DialogHeader>
                <DialogTitle>Your new backup codes</DialogTitle>
                <DialogDescription>Your old codes no longer work. Save these — shown only once.</DialogDescription>
              </DialogHeader>
              <div className="mt-4"><BackupCodes codes={codes} /></div>
              <DialogFooter className="mt-5"><Button type="button" onClick={finishRegen} data-testid="mfa-regen-done">Done</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable flow */}
      <Dialog open={dialog === "disable"} onOpenChange={closeDialog}>
        <DialogContent data-testid="mfa-disable-dialog">
          <form onSubmit={disableMfa}>
            <DialogHeader>
              <DialogTitle>Disable two-factor authentication</DialogTitle>
              <DialogDescription>This removes 2FA and invalidates your backup codes. Confirm your password and a current code.</DialogDescription>
            </DialogHeader>
            {manageFields}
            <DialogFooter className="mt-5"><Button type="submit" variant="destructive" disabled={busy || !password || code.length < 6} data-testid="mfa-disable-submit">{busy ? "Please wait…" : "Disable 2FA"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
